import React from 'react';
import { Box, Typography } from '@mui/material';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import { ToolbarButton } from '../inputs/ToolbarButton';
import { DownloadButton } from '../inputs/DownloadButton';

export interface WorkbookCardSheet {
  name: string;
  rowCount: number;
}

export interface WorkbookCardProps {
  title: string;
  sheets: WorkbookCardSheet[];
  /** 1 for the first build; later versions say so, since the earlier file still exists. */
  version?: number;
  /** Downloads the file. The card shows its own progress and failure. */
  onDownload: () => Promise<void> | void;
  /** Opens the workbook to look at. Omitted, the card offers no preview. */
  onPreview?: () => void;
  /** Said when a download fails, in place of the sheet list. */
  error?: string | null;
}

const rowLabel = (n: number) => `${n.toLocaleString()} row${n === 1 ? '' : 's'}`;

/**
 * A workbook the agent built, offered in the conversation: what is in it,
 * sheet by sheet, and one button to take it away.
 *
 * The sheets are listed rather than counted because the sheet names are the
 * answer to "what did you build?" — someone scanning the chat later should not
 * have to open the file to find out.
 */
export function WorkbookCard({ title, sheets, version, onDownload, onPreview, error }: WorkbookCardProps) {
  const [busy, setBusy] = React.useState(false);
  const totalRows = sheets.reduce((sum, s) => sum + s.rowCount, 0);

  return (
    <Box
      sx={(t) => ({
        border: `1px solid ${t.tokens.border.default}`,
        borderRadius: `${t.ds.radius.md}px`,
        bgcolor: t.tokens.surface.raised,
        overflow: 'hidden',
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5 }}>
        <Box
          aria-hidden
          sx={(t) => ({
            width: t.ds.sizes.control.md,
            height: t.ds.sizes.control.md,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            borderRadius: `${t.ds.radius.sm}px`,
            bgcolor: t.tokens.brand.primarySoft,
            color: t.tokens.text.accent,
          })}
        >
          <TableChartOutlined fontSize="small" />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2">{title}</Typography>
          <Typography variant="caption" component="div" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {[
              `${sheets.length} sheet${sheets.length === 1 ? '' : 's'}`,
              totalRows > 0 ? rowLabel(totalRows) : '',
              version && version > 1 ? `v${version}` : '',
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>

        {onPreview && (
          <ToolbarButton icon={<VisibilityOutlined fontSize="small" />} onClick={onPreview}>
            Preview
          </ToolbarButton>
        )}

        {/* The kit's one download control, so this reads like every other
            download in the product: one format, so no menu. */}
        <DownloadButton
          loading={busy}
          tooltip="Download this workbook as Excel"
          formats={[
            {
              key: 'xlsx',
              label: 'Excel (.xlsx)',
              onDownload: async () => {
                setBusy(true);
                try {
                  await onDownload();
                } finally {
                  setBusy(false);
                }
              },
            },
          ]}
        />
      </Box>

      {error ? (
        <Box sx={(t) => ({ px: 1.5, py: 1, borderTop: `1px solid ${t.tokens.border.subtle}` })}>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>
            {error}
          </Typography>
        </Box>
      ) : (
        sheets.length > 0 && (
          <Box
            component="ul"
            sx={(t) => ({
              listStyle: 'none',
              m: 0,
              p: 0,
              borderTop: `1px solid ${t.tokens.border.subtle}`,
              bgcolor: t.tokens.surface.sunken,
            })}
          >
            {sheets.map((sheet) => (
              <Box
                component="li"
                key={sheet.name}
                sx={(t) => ({
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  px: 1.5,
                  py: 0.5,
                  '& + li': { borderTop: `1px solid ${t.tokens.border.subtle}` },
                })}
              >
                <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary, minWidth: 0 })}>
                  {sheet.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={(t) => ({ color: t.tokens.text.tertiary, flexShrink: 0, fontVariantNumeric: 'tabular-nums' })}
                >
                  {rowLabel(sheet.rowCount)}
                </Typography>
              </Box>
            ))}
          </Box>
        )
      )}
    </Box>
  );
}
